// Copyright (c) Ping. All rights reserved.

import React, { useCallback, useMemo } from 'react';
import {
  Pressable,
  StyleSheet,
  Text,
  type TextStyle,
  View,
  type ViewStyle,
} from 'react-native';
import { useColorScheme } from '../../foundations/colorScheme';
import { createTheme } from '../../design-system/theme';
import { PollOptionRow } from './PollOptionRow';
import { pollStrings } from './strings';
import type { Poll, PollOptionId } from './types';
import { pollProgressFractions, winningOptionIds } from './usePollComposerViewModel';

export interface PollCardProps {
  readonly poll: Poll;
  readonly onVote?: ((optionId: PollOptionId) => void) | undefined;
  readonly onViewVotes?: ((poll: Poll) => void) | undefined;
}

/**
 * In-chat poll card. Pure presentation. Vote actions are gated by the
 * poll's lifecycle (`open` only) and routed back through `onVote`.
 *
 * `single`-kind polls call `onVote` and the host is expected to replace any
 * existing selection; `multiple`-kind polls call `onVote` to toggle the
 * selection. The card never mutates the poll record itself — the host owns
 * the source of truth.
 */
export const PollCard = React.memo<PollCardProps>(({ poll, onVote, onViewVotes }) => {
  const scheme = useColorScheme();
  const theme = createTheme(scheme === 'dark' ? 'dark' : 'light');

  const fractions = useMemo(() => pollProgressFractions(poll), [poll]);
  const winnerSet = useMemo(() => {
    if (poll.state === 'open') {
      return new Set<string>();
    }
    return new Set(winningOptionIds(poll.options));
  }, [poll]);

  const handleVote = useCallback(
    (optionId: PollOptionId) => {
      if (poll.state !== 'open') {
        return;
      }
      onVote?.(optionId);
    },
    [poll.state, onVote],
  );

  const handleViewVotes = useCallback(() => {
    onViewVotes?.(poll);
  }, [poll, onViewVotes]);

  return (
    <View
      style={[
        styles.container,
        {
          backgroundColor: theme.colors.surface.primary,
          borderColor: theme.colors.border.secondary,
        },
      ]}
      accessibilityLabel={pollStrings.cardStateBadge(poll.state)}
    >
      <View style={styles.header}>
        <Text style={[styles.question, { color: theme.colors.text.primary }]} numberOfLines={3}>
          {poll.question}
        </Text>
        <View
          style={[
            styles.stateBadge,
            { backgroundColor: badgeColor(poll.state, theme.colors.accent) },
          ]}
        >
          <Text style={styles.stateBadgeText}>{pollStrings.cardStateBadge(poll.state)}</Text>
        </View>
      </View>
      <View style={styles.optionsList}>
        {poll.options.map((opt, idx) => (
          <PollOptionRow
            key={opt.id}
            index={idx}
            text={opt.text}
            voteCount={opt.voteCount}
            votedByMe={opt.votedByMe}
            mode={{
              kind: 'card',
              state: poll.state,
              fraction: fractions[idx] ?? 0,
              isWinner: winnerSet.has(opt.id),
            }}
            onPress={() => handleVote(opt.id)}
          />
        ))}
      </View>
      <View style={styles.footer}>
        <Text style={[styles.footerCount, { color: theme.colors.text.secondary }]}>
          {pollStrings.cardTotalVotes(poll.totalVotes)}
        </Text>
        <Pressable
          onPress={handleViewVotes}
          accessibilityRole="button"
          accessibilityLabel={pollStrings.cardViewVotes}
          style={({ pressed }) => [styles.footerAction, pressed && { opacity: 0.6 }]}
        >
          <Text style={[styles.footerActionText, { color: theme.colors.accent.indigo }]}>
            {pollStrings.cardViewVotes}
          </Text>
        </Pressable>
      </View>
    </View>
  );
});

PollCard.displayName = 'PollCard';

function badgeColor(
  state: Poll['state'],
  accent: { indigo: string; amber: string; slate: string },
): string {
  switch (state) {
    case 'open':
      return accent.indigo;
    case 'closed':
      return accent.slate;
    case 'tied':
      return accent.amber;
  }
}

const styles = StyleSheet.create({
  container: {
    borderWidth: 1,
    borderRadius: 12,
    padding: 14,
    marginVertical: 6,
    maxWidth: 480,
  } satisfies ViewStyle,
  header: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 12,
  } satisfies ViewStyle,
  question: {
    flex: 1,
    fontSize: 15,
    fontWeight: '600',
    lineHeight: 20,
    marginRight: 10,
  } satisfies TextStyle,
  stateBadge: {
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 10,
  } satisfies ViewStyle,
  stateBadgeText: {
    color: '#FFFFFF',
    fontSize: 11,
    fontWeight: '700',
    textTransform: 'uppercase',
  } satisfies TextStyle,
  optionsList: {
    marginBottom: 8,
  } satisfies ViewStyle,
  footer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: 4,
  } satisfies ViewStyle,
  footerCount: {
    fontSize: 13,
    fontWeight: '500',
  } satisfies TextStyle,
  footerAction: {
    paddingVertical: 4,
    paddingHorizontal: 6,
  } satisfies ViewStyle,
  footerActionText: {
    fontSize: 13,
    fontWeight: '600',
  } satisfies TextStyle,
});
